/**
 * Otel detay sayfası için Schema.org Hotel JSON-LD schema üretir (server veya client'ta kullanılabilir).
 * Görsel kuralı: Kaç tane image gelirse gelsin en fazla 5 tanesi alınır.
 *
 * @param {Object} hotelData - API'den gelen otel detay verisi (HotelDetailPage'deki hotelData)
 * @param {Object} options
 * @param {string} [options.baseUrl] - Site base URL. Varsayılan: appConfig.SITE_URL
 * @param {string} [options.locale] - Locale (path için). Varsayılan: "tr"
 * @param {string} [options.hotelSlug] - Otel slug (URL segment). Zorunlu url için
 * @param {string} [options.priceRangeText] - priceRange metni. Varsayılan: "en iyi fiyatlarla"
 * @returns {Object|null} Schema.org Hotel JSON-LD objesi veya hotelData yoksa null
 */

import appConfig from "@/configs/app-config";
import { getLocalizedPath } from "@/utils/pathUtils";

const HOTEL_DETAIL_SCHEMA_MAX_IMAGES = 5;

function stripHtml(html) {
  if (!html || typeof html !== "string") return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export function buildHotelDetailSchema(hotelData, options = {}) {
  if (!hotelData || typeof hotelData !== "object") return null;

  const {
    baseUrl = appConfig.SITE_URL,
    locale = "tr",
    hotelSlug = "",
    priceRangeText = "en iyi fiyatlarla",
  } = options;

  const hotelPath = `${getLocalizedPath("/hotel", locale)}/${hotelSlug}`.replace(/\/+/g, "/");
  const url = hotelSlug ? `${baseUrl}${hotelPath}` : undefined;

  // Image: max 5 (Medias[].Url)
  const medias = Array.isArray(hotelData.Medias) ? hotelData.Medias : [];
  const imageUrls = medias
    .slice(0, HOTEL_DETAIL_SCHEMA_MAX_IMAGES)
    .map((m) => m?.Url)
    .filter(Boolean);
  const image = imageUrls.map((src) =>
    src && src.startsWith("http") ? src : src ? `${baseUrl}${src}` : null
  ).filter(Boolean);
  const imageForSchema = image.length > 0 ? image : undefined;

  // Description: Summary / Description / ilk Descriptions Content (HTML sadeleştirilir)
  let description = hotelData.Summary || hotelData.Description || "";
  if (!description && Array.isArray(hotelData.Descriptions) && hotelData.Descriptions.length > 0) {
    const first = hotelData.Descriptions.find((d) => d?.Content);
    description = first ? first.Content : "";
  }
  description = stripHtml(description);
  if (!description) description = undefined;

  // Address
  const addr = hotelData.Address || {};
  const address = {
    "@type": "PostalAddress",
    streetAddress: addr.StreetAddress || addr.streetAddress || hotelData.District || "",
    addressLocality: addr.City || addr.city || hotelData.City || "",
    addressRegion: addr.Region || addr.region || hotelData.Region || hotelData.RegionName || "",
    postalCode: addr.PostalCode || addr.postalCode || "",
    addressCountry: addr.Country || addr.country || hotelData.Country || "TR",
  };
  // Boş alanları çıkarma
  Object.keys(address).forEach((k) => {
    if (k === "@type") return;
    if (!address[k]) delete address[k];
  });

  // Geo
  const lat = hotelData.Latitude ?? addr.Latitude;
  const lng = hotelData.Longitude ?? addr.Longitude;
  const geo =
    lat != null && lng != null && (lat !== "" || lng !== "")
      ? {
        "@type": "GeoCoordinates",
        latitude: String(lat),
        longitude: String(lng),
      }
      : undefined;

  // starRating
  const starNum = hotelData.StarNumber ?? addr.StarNumber;
  const starRating =
    starNum != null && starNum !== ""
      ? { "@type": "Rating", ratingValue: String(starNum) }
      : undefined;

  // aggregateRating
  const surveyScore = hotelData.SurveyScore;
  const reviewCount = hotelData.ReviewCount;
  const aggregateRating =
    surveyScore != null &&
      Number(surveyScore) > 0 &&
      reviewCount != null &&
      Number(reviewCount) >= 0
      ? {
        "@type": "AggregateRating",
        ratingValue: String(surveyScore),
        bestRating: "10",
        worstRating: "1",
        reviewCount: String(reviewCount),
      }
      : undefined;

  // amenityFeature (HotelAmenities)
  const amenities = Array.isArray(hotelData.HotelAmenities) ? hotelData.HotelAmenities : [];
  const amenityFeature =
    amenities.length > 0
      ? amenities.map((a) => ({
        "@type": "LocationFeatureSpecification",
        name: a.AmenityName || a.name || "",
        value: true,
      })).filter((a) => a.name)
      : undefined;

  // checkinTime / checkoutTime (HotelRule)
  const rule = hotelData.HotelRule || {};
  const checkinTime = rule.CheckinTime || rule.checkinTime || undefined;
  const checkoutTime = rule.CheckoutTime || rule.checkoutTime || undefined;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Hotel",
    name: hotelData.HotelName || hotelData.Name || "Otel",
    ...(url && { url }),
    ...(imageForSchema && imageForSchema.length > 0 && { image: imageForSchema }),
    ...(description && { description }),
    ...(hotelData.Telephone || hotelData.PhoneNumber || addr.Phone
      ? { telephone: hotelData.Telephone || hotelData.PhoneNumber || addr.Phone }
      : {}),
    priceRange: priceRangeText,
    address,
    ...(geo && { geo }),
    ...(starRating && { starRating }),
    ...(aggregateRating && { aggregateRating }),
    ...(amenityFeature && amenityFeature.length > 0 && { amenityFeature }),
    ...(checkinTime && { checkinTime }),
    ...(checkoutTime && { checkoutTime }),
  };

  return schema;
}
